import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Field, Select } from '../components/Field'
import { PubBadge } from '../components/PubBadge'
import { StatusDot } from '../components/StatusDot'
import { api, type Subscriber } from '../api/client'
import { fmtRelative, fmtName } from '../lib/format'

type Filter = 'all' | 'active' | 'expired' | 'suppressed' | 'deleted_upstream'
type PubFilter = 'all' | 'rc_car_action' | 'flight_journal' | 'model_airplane_news' | 'diecast'
type AutoRenewFilter = 'all' | 'on' | 'off'
type PaymentTypeFilter = 'all' | 'paypal' | 'authorize' | 'bank' | 'stripe' | 'agency' | 'comp' | 'other'

const PUB_OPTIONS: Array<{ value: PubFilter; label: string }> = [
  { value: 'all', label: 'All Titles' },
  { value: 'rc_car_action', label: 'RC Car Action' },
  { value: 'flight_journal', label: 'Flight Journal' },
  { value: 'model_airplane_news', label: 'Model Airplane News' },
  { value: 'diecast', label: 'Die Cast X' },
]

export function SubscribersPage() {
  const [q, setQ] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [pub, setPub] = useState<PubFilter>('all')
  const [autoRenew, setAutoRenew] = useState<AutoRenewFilter>('all')
  const [paymentType, setPaymentType] = useState<PaymentTypeFilter>('all')

  const { data, isLoading } = useQuery({
    queryKey: ['subscribers', q, filter, pub, autoRenew, paymentType],
    queryFn: () =>
      api.get<{ total: number; items: (Subscriber & { subscription_summary: string[] })[] }>(
        `/api/subscribers?q=${encodeURIComponent(q)}&filter=${filter}&publication=${pub}&auto_renew=${autoRenew}&payment_type=${paymentType}&limit=100`,
      ),
    staleTime: 2_000,
  })

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Registry</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            Subscribers
          </h1>
        </div>
        <div className="text-right">
          <div className="label">Matching</div>
          <div className="font-mono text-2xl text-amber-glow tabular-nums mt-0.5">
            {data?.total ?? '—'}
          </div>
        </div>
      </header>

      <Panel label="Query" accent="amber">
        <div className="grid grid-cols-12 gap-4 items-end">
          <div className="col-span-4">
            <Field
              label="Search (email / name / SimpleCirc ID)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onClear={() => setQ('')}
              placeholder="alice@cirrata.com  or  #42001"
            />
          </div>
          <div className="col-span-2">
            <Select
              label="Title"
              value={pub}
              onChange={(e) => setPub(e.target.value as PubFilter)}
              options={PUB_OPTIONS}
            />
          </div>
          <div className="col-span-2">
            <Select
              label="Status"
              value={filter}
              onChange={(e) => setFilter(e.target.value as Filter)}
              options={[
                { value: 'all', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'expired', label: 'Expired' },
                { value: 'suppressed', label: 'Suppressed' },
                { value: 'deleted_upstream', label: 'Deleted upstream' },
              ]}
            />
          </div>
          <div className="col-span-2">
            <Select
              label="Auto renew"
              value={autoRenew}
              onChange={(e) => setAutoRenew(e.target.value as AutoRenewFilter)}
              options={[
                { value: 'all', label: 'All' },
                { value: 'on', label: 'On' },
                { value: 'off', label: 'Off' },
              ]}
            />
          </div>
          <div className="col-span-2">
            <Select
              label="Payment type"
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value as PaymentTypeFilter)}
              options={[
                { value: 'all', label: 'All' },
                { value: 'paypal', label: 'PayPal' },
                { value: 'authorize', label: 'Authorize.Net' },
                { value: 'bank', label: 'Bank (eCheck)' },
                { value: 'stripe', label: 'Stripe' },
                { value: 'agency', label: 'Agency' },
                { value: 'comp', label: 'Comp / Gift' },
                { value: 'other', label: 'Other / none' },
              ]}
            />
          </div>
        </div>
      </Panel>

      <Panel label="Results" bodyClassName="p-0">
        {isLoading && (
          <div className="px-4 py-12 text-center label text-paper-trace">Loading…</div>
        )}
        {!isLoading && (data?.items.length ?? 0) === 0 && (
          <div className="px-4 py-12 text-center label text-paper-trace">No matches</div>
        )}
        {!isLoading && (data?.items.length ?? 0) > 0 && (
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-500">
                <Th>Account</Th>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Location</Th>
                <Th>Subscriptions</Th>
                <Th>Last Sync</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {data!.items.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-ink-500/40 hover:bg-ink-700/50 transition-colors"
                >
                  <Td mono>
                    {/* The AAM Member ID is the account number we print, quote
                        and take over the phone. simplecirc_account_id only
                        stands in for the handful of rows that predate the mint
                        — for a site purchase it holds a gateway key
                        ("stripe-cus_…", "authnet-aam-…"), which is not an
                        account number anyone can use. */}
                    <span className="text-paper-dim">#</span>
                    <span className="text-paper">
                      {s.member_account_number || s.simplecirc_account_id}
                    </span>
                    {s.email_suppressed_at && (
                      <StatusDot tone="err" />
                    )}
                    {s.upstream_deleted_at && (
                      <span
                        className="ml-1.5 rounded px-1 py-px text-[10px] uppercase tracking-wide bg-err/15 text-err"
                        title={`Deleted from SimpleCirc — ${s.upstream_deleted_reason || 'no longer returned by the API'}`}
                      >
                        deleted
                      </span>
                    )}
                  </Td>
                  <Td>{fmtName(s.first_name, s.last_name)}</Td>
                  <Td mono subtle>
                    {s.email || <span className="text-paper-trace italic">—</span>}
                  </Td>
                  <Td mono subtle>
                    {[s.city, s.state].filter(Boolean).join(', ') || '—'}
                  </Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      {s.subscription_summary.length === 0 && (
                        <span className="label text-paper-trace">none</span>
                      )}
                      {s.subscription_summary.map((slug) => (
                        <PubBadge key={slug} slug={slug} compact />
                      ))}
                    </div>
                  </Td>
                  <Td mono subtle>{fmtRelative(s.last_synced_at)}</Td>
                  <Td>
                    <Link
                      to={`/subscribers/${s.id}`}
                      className="label-amber hover:underline"
                    >
                      Open →
                    </Link>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  )
}

function Th({ children }: { children?: React.ReactNode }) {
  return (
    <th className="label py-2 px-4 font-normal text-left first:pl-4 last:pr-4">
      {children}
    </th>
  )
}

function Td({
  children, mono, subtle, className = '',
}: {
  children: React.ReactNode
  mono?: boolean
  subtle?: boolean
  className?: string
}) {
  return (
    <td
      className={`py-2.5 px-4 align-middle
        ${mono ? 'font-mono text-[11px] tabular-nums' : 'text-[13px]'}
        ${subtle ? 'text-paper-dim' : 'text-paper'}
        ${className}`}
    >
      {mono ? (
        <span className="flex items-center gap-2">{children}</span>
      ) : (
        children
      )}
    </td>
  )
}
